import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import { Filter, Search, X, Calendar, RefreshCw, Pencil, Trash2, AlertTriangle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

export function InventoryLogs({ products, categories }) {
    const { t, language } = useLanguage();
    const { user } = useAuth();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filters
    const [typeFilter, setTypeFilter] = useState('all');
    const [productFilter, setProductFilter] = useState('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);

    // Edit/Delete state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [editForm, setEditForm] = useState({
        quantity: '',
        price: '',
        reference: '',
        notes: '',
        date: ''
    });

    const isAdmin = user?.role === 'ADMIN';

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page, limit: 20 });
            if (typeFilter !== 'all') params.append('type', typeFilter);
            if (productFilter !== 'all') params.append('productId', productFilter);
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);

            const res = await axios.get(`${API_URL}/inventory/logs?${params}`, { headers });
            setLogs(res.data.data.transactions || []);
            setTotalPages(res.data.data.pagination.pages || 1);
            setTotal(res.data.data.pagination.total || 0);
        } catch (error) {
            console.error('Failed to fetch inventory logs:', error);
        } finally {
            setLoading(false);
        }
    }, [typeFilter, productFilter, startDate, endDate, page]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const clearFilters = () => {
        setTypeFilter('all');
        setProductFilter('all');
        setStartDate('');
        setEndDate('');
        setPage(1);
    };

    const hasActiveFilters = typeFilter !== 'all' || productFilter !== 'all' || startDate || endDate;

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Delete handlers
    const openDeleteDialog = (log) => {
        setSelectedLog(log);
        setDeleteDialogOpen(true);
    };

    const handleDelete = async () => {
        if (!selectedLog) return;
        setActionLoading(true);
        try {
            await axios.delete(`${API_URL}/inventory/logs/${selectedLog.id}`, { headers });
            setDeleteDialogOpen(false);
            setSelectedLog(null);
            fetchLogs();
        } catch (error) {
            console.error('Delete failed:', error);
            toast.error(error.response?.data?.error || 'Failed to delete transaction');
        } finally {
            setActionLoading(false);
        }
    };

    // Edit handlers
    const openEditDialog = (log) => {
        setSelectedLog(log);
        setEditForm({
            quantity: log.quantity.toString(),
            price: parseFloat(log.price).toString(),
            reference: log.reference || '',
            notes: log.notes || '',
            date: new Date(log.date).toISOString().split('T')[0]
        });
        setEditDialogOpen(true);
    };

    const handleEdit = async () => {
        if (!selectedLog) return;
        setActionLoading(true);
        try {
            await axios.put(`${API_URL}/inventory/logs/${selectedLog.id}`, {
                quantity: parseInt(editForm.quantity),
                price: parseFloat(editForm.price),
                reference: editForm.reference,
                notes: editForm.notes,
                date: editForm.date
            }, { headers });
            setEditDialogOpen(false);
            setSelectedLog(null);
            fetchLogs();
        } catch (error) {
            console.error('Edit failed:', error);
            toast.error(error.response?.data?.error || 'Failed to edit transaction');
        } finally {
            setActionLoading(false);
        }
    };

    const getTypeLabel = (type) => {
        if (type === 'BUY') return 'Buy (Purchase)';
        if (type === 'SELL') return 'Sell (Direct Sale)';
        if (type === 'RETURN') return 'Return';
        return type;
    };

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader className="pb-3 flex flex-row items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Filter className="h-4 w-4" />
                        {t('filters') || 'Filters'}
                        {hasActiveFilters && (
                            <Badge variant="secondary" className="ml-2">
                                Active
                            </Badge>
                        )}
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={fetchLogs}>
                        <RefreshCw className="h-4 w-4 mr-2" /> Refresh
                    </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <Select value={typeFilter} onValueChange={setTypeFilter}>
                            <SelectTrigger>
                                <SelectValue placeholder="All Types" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Types</SelectItem>
                                <SelectItem value="BUY">Bought (In)</SelectItem>
                                <SelectItem value="SELL">Sold (Out)</SelectItem>
                                <SelectItem value="RETURN">Returned (In)</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={productFilter} onValueChange={setProductFilter}>
                            <SelectTrigger>
                                <SelectValue placeholder="All Products" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Products</SelectItem>
                                {products?.map((p) => (
                                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                <Calendar className="h-3 w-3" /> {t('dateFrom') || 'Date From'}
                            </Label>
                            <Input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                <Calendar className="h-3 w-3" /> {t('dateTo') || 'Date To'}
                            </Label>
                            <Input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </div>
                    </div>
                    {hasActiveFilters && (
                        <Button variant="outline" size="sm" onClick={clearFilters}>
                            <X className="h-4 w-4 mr-2" />
                            Clear Filters
                        </Button>
                    )}
                </CardContent>
            </Card>

            <div className="border rounded-lg overflow-hidden bg-background">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Product</TableHead>
                            <TableHead className="text-right">Qty</TableHead>
                            <TableHead className="text-right">Price</TableHead>
                            <TableHead className="text-right">Total</TableHead>
                            <TableHead>User / Reference</TableHead>
                            {isAdmin && <TableHead className="text-center">Actions</TableHead>}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={isAdmin ? 8 : 7} className="text-center py-8">Loading...</TableCell>
                            </TableRow>
                        ) : logs.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={isAdmin ? 8 : 7} className="text-center py-8 text-muted-foreground">
                                    No operations found
                                </TableCell>
                            </TableRow>
                        ) : (
                            logs.map((log) => (
                                <TableRow key={log.id}>
                                    <TableCell className="text-sm whitespace-nowrap">
                                        {formatDate(log.date)}
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant={log.type === 'BUY' ? 'success' : log.type === 'RETURN' ? 'warning' : 'default'} className="whitespace-nowrap">
                                            {log.type === 'BUY' ? 'IN (BUY)' : log.type === 'RETURN' ? 'RETURN (IN)' : 'OUT (SELL)'}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <div className="font-medium">{log.product?.name}</div>
                                        <div className="text-xs text-muted-foreground">{log.product?.sku}</div>
                                    </TableCell>
                                    <TableCell className="text-right font-bold">
                                        {log.type === 'SELL' ? '-' : '+'}{log.quantity}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        {parseFloat(log.price).toFixed(2)} TND
                                    </TableCell>
                                    <TableCell className="text-right font-bold">
                                        {log.type === 'RETURN' ? '-' : ''}{parseFloat(log.totalAmount).toFixed(2)} TND
                                    </TableCell>
                                    <TableCell>
                                        <div className="text-sm">{log.user?.name}</div>
                                        <div className="text-xs text-muted-foreground">{log.reference || log.notes}</div>
                                    </TableCell>
                                    {isAdmin && (
                                        <TableCell className="text-center">
                                            <div className="flex items-center justify-center gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-muted-foreground hover:text-primary"
                                                    onClick={() => openEditDialog(log)}
                                                    title="Edit"
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                                    onClick={() => openDeleteDialog(log)}
                                                    title="Delete"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between pt-4">
                    <div className="text-sm text-muted-foreground">
                        Showing page <span className="font-medium">{page}</span> of <span className="font-medium">{totalPages}</span> ({total} total operations)
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={page === 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                            Previous
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={page === totalPages}
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        >
                            Next
                        </Button>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-destructive">
                            <AlertTriangle className="h-5 w-5" />
                            Delete Operation
                        </DialogTitle>
                        <DialogDescription>
                            This will permanently delete this {selectedLog ? getTypeLabel(selectedLog.type) : ''} operation and reverse all its effects on inventory and finances.
                        </DialogDescription>
                    </DialogHeader>
                    {selectedLog && (
                        <div className="space-y-3 py-2">
                            <div className="rounded-lg border p-3 space-y-1 text-sm">
                                <div><span className="text-muted-foreground">Type:</span> <span className="font-medium">{getTypeLabel(selectedLog.type)}</span></div>
                                <div><span className="text-muted-foreground">Product:</span> <span className="font-medium">{selectedLog.product?.name}</span></div>
                                <div><span className="text-muted-foreground">Quantity:</span> <span className="font-medium">{selectedLog.quantity}</span></div>
                                <div><span className="text-muted-foreground">Price:</span> <span className="font-medium">{parseFloat(selectedLog.price).toFixed(2)} TND</span></div>
                                <div><span className="text-muted-foreground">Total:</span> <span className="font-medium">{parseFloat(selectedLog.totalAmount).toFixed(2)} TND</span></div>
                                <div><span className="text-muted-foreground">Date:</span> <span className="font-medium">{formatDate(selectedLog.date)}</span></div>
                            </div>
                            <div className="bg-destructive/10 text-destructive text-sm rounded-lg p-3">
                                <strong>Warning:</strong> This action cannot be undone. The associated inventory units, expenses, and orders will be reversed.
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={actionLoading}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleDelete} disabled={actionLoading}>
                            {actionLoading ? (
                                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Deleting...</>
                            ) : (
                                <><Trash2 className="h-4 w-4 mr-2" /> Delete & Reverse</>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                <DialogContent className="sm:max-w-[480px]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Pencil className="h-5 w-5" />
                            Edit Operation
                        </DialogTitle>
                        <DialogDescription>
                            Modify this {selectedLog ? getTypeLabel(selectedLog.type) : ''} operation. Changes will automatically adjust inventory and finances.
                        </DialogDescription>
                    </DialogHeader>
                    {selectedLog && (
                        <div className="space-y-4 py-2">
                            <div className="flex items-center gap-2 text-sm">
                                <Badge variant={selectedLog.type === 'BUY' ? 'success' : selectedLog.type === 'RETURN' ? 'warning' : 'default'}>
                                    {getTypeLabel(selectedLog.type)}
                                </Badge>
                                <span className="font-medium">{selectedLog.product?.name}</span>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="edit-quantity">Quantity</Label>
                                    <Input
                                        id="edit-quantity"
                                        type="number"
                                        min="1"
                                        value={editForm.quantity}
                                        onChange={(e) => setEditForm(prev => ({ ...prev, quantity: e.target.value }))}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="edit-price">Price (TND)</Label>
                                    <Input
                                        id="edit-price"
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={editForm.price}
                                        onChange={(e) => setEditForm(prev => ({ ...prev, price: e.target.value }))}
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="edit-reference">{selectedLog.type === 'BUY' ? 'Supplier' : 'Customer / Reference'}</Label>
                                <Input
                                    id="edit-reference"
                                    value={editForm.reference}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, reference: e.target.value }))}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="edit-notes">Notes</Label>
                                <Input
                                    id="edit-notes"
                                    value={editForm.notes}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="edit-date">Date</Label>
                                <Input
                                    id="edit-date"
                                    type="date"
                                    value={editForm.date}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, date: e.target.value }))}
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={actionLoading}>
                            Cancel
                        </Button>
                        <Button onClick={handleEdit} disabled={actionLoading}>
                            {actionLoading ? (
                                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                            ) : (
                                'Save Changes'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
